/**
 * Full-page gate shown when Firebase authenticated the user but the server-side
 * profile never arrived.
 *
 * This replaces the application rather than sitting above it. Rendering the app
 * in this state means every account-scoped screen — library, playlists,
 * history, settings — draws itself empty and every save silently fails, so the
 * user is left to discover the outage one dead button at a time. Blocking here
 * states the problem once, at the only moment it can still be acted on.
 *
 * The Firebase session is deliberately left intact so Retry can re-run
 * `POST /auth/sync` without another trip through the provider popup.
 */

import { useAuth } from '../../providers/AuthProvider.jsx';
import { Button } from '../ui/Button.jsx';
import { describeSyncError } from './syncErrorCopy.js';
import styles from './AccountUnavailable.module.css';

export function AccountUnavailable() {
  const { syncError, retryingSync, retrySync, signOut } = useAuth();
  const { isAuthProblem, message, detail } = describeSyncError(syncError);

  return (
    <div className={styles.container}>
      <div className={styles.card} role="alert" aria-live="assertive">
        <h1 className={styles.title}>We could not load your account</h1>
        <p className={styles.message}>{message}</p>
        {detail && <code className={styles.detail}>{detail}</code>}

        <div className={styles.actions}>
          <Button variant="primary" loading={retryingSync} onClick={retrySync}>
            Try again
          </Button>

          {/* Always offered, not just for auth failures: without it a user
              whose sync keeps failing has no way out of this screen except
              clearing site data. */}
          <Button variant="ghost" onClick={signOut}>
            {isAuthProblem ? 'Sign in again' : 'Sign out'}
          </Button>
        </div>
      </div>
    </div>
  );
}
